import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Heart, Gift, Users, Filter } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export type StampFilterType = 'all' | 'visit' | 'referral'

interface StampFilterProps {
  currentFilter: StampFilterType
  onFilterChange: (filter: StampFilterType) => void
  visitCount: number
  referralCount: number
  totalCount: number
}

const StampFilter = ({ 
  currentFilter, 
  onFilterChange, 
  visitCount, 
  referralCount, 
  totalCount 
}: StampFilterProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Filter className="h-5 w-5" />
          Stempel-Filter
        </CardTitle>
        <CardDescription>
          Filtern Sie die Ansicht nach Stempeltyp
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-4">
          {/* Filter Stats */}
          <div className="grid grid-cols-3 gap-2">
            <div className="p-3 bg-muted/50 rounded-lg text-center">
              <Users className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
              <div className="text-lg font-bold">{totalCount}</div>
              <div className="text-xs text-muted-foreground">Gesamt</div>
            </div>
            
            <div className="p-3 bg-primary/5 rounded-lg text-center">
              <Heart className="h-5 w-5 mx-auto mb-1 text-primary" />
              <div className="text-lg font-bold text-primary">{visitCount}</div>
              <div className="text-xs text-muted-foreground">Besuche</div>
            </div>
            
            <div className="p-3 bg-accent/5 rounded-lg text-center">
              <Gift className="h-5 w-5 mx-auto mb-1 text-accent" />
              <div className="text-lg font-bold text-accent">{referralCount}</div>
              <div className="text-xs text-muted-foreground">Empfehlungen</div>
            </div>
          </div>

          {/* Filter Select */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Stempeltyp anzeigen:</label>
            <Select value={currentFilter} onValueChange={(value: StampFilterType) => onFilterChange(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Alle Stempel ({totalCount})
                  </div>
                </SelectItem>
                <SelectItem value="visit">
                  <div className="flex items-center gap-2">
                    <Heart className="h-4 w-4 text-primary" />
                    Nur Besuche ({visitCount})
                  </div>
                </SelectItem>
                <SelectItem value="referral">
                  <div className="flex items-center gap-2">
                    <Gift className="h-4 w-4 text-accent" />
                    Nur Empfehlungen ({referralCount})
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Quick Filter Buttons */}
          <div className="flex flex-col gap-2">
            <Button
              variant={currentFilter === 'all' ? 'default' : 'outline'}
              onClick={() => onFilterChange('all')}
              className="justify-start"
            >
              <Users className="h-4 w-4 mr-2" />
              Alle anzeigen
              <Badge variant="secondary" className="ml-auto">
                {totalCount}
              </Badge>
            </Button>
            
            <Button
              variant={currentFilter === 'visit' ? 'default' : 'outline'}
              onClick={() => onFilterChange('visit')}
              className="justify-start"
            >
              <Heart className="h-4 w-4 mr-2 text-primary" />
              Nur Besuche
              <Badge variant="secondary" className="ml-auto">
                {visitCount}
              </Badge>
            </Button>
            
            <Button
              variant={currentFilter === 'referral' ? 'default' : 'outline'}
              onClick={() => onFilterChange('referral')}
              className="justify-start"
            >
              <Gift className="h-4 w-4 mr-2 text-accent" />
              Nur Empfehlungen
              <Badge variant="secondary" className="ml-auto">
                {referralCount}
              </Badge>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default StampFilter